import { useState, useMemo } from 'react'
import { Search, Eye, EyeOff, Copy, Download, Database, Check, Shield } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

interface ProfileItem {
  id: string
  name: string
  fb_email?: string | null
  fb_password?: string | null
  fb_user_id?: string | null
  proxy?: string | null
  status?: string | null
  created_at?: number
}

interface DatabaseTabProps {
  profiles: ProfileItem[]
}

export function DatabaseTab({ profiles }: DatabaseTabProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const filteredProfiles = useMemo(() => {
    if (!searchTerm.trim()) return profiles
    const term = searchTerm.toLowerCase().trim()
    return profiles.filter(p =>
      (p.name && p.name.toLowerCase().includes(term)) ||
      (p.fb_email && p.fb_email.toLowerCase().includes(term)) ||
      (p.fb_user_id && p.fb_user_id.toLowerCase().includes(term)) ||
      (p.proxy && p.proxy.toLowerCase().includes(term))
    )
  }, [profiles, searchTerm])

  const copyToClipboard = (text: string, fieldKey: string, label: string) => {
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldKey)
      toast.success(`${label} copiado!`)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }

  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const exportTxt = () => {
    if (profiles.length === 0) {
      toast.error('Nenhuma conta para exportar')
      return
    }

    const lines = profiles.map(p => {
      const email = p.fb_email || p.name || 'Sem Email'
      const pass = p.fb_password || 'Sem Senha'
      const uid = p.fb_user_id || 'Sem UID'
      const px = p.proxy || 'Sem Proxy'
      return `${email} | ${pass} | UID: ${uid} | Proxy: ${px}`
    })

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `banco_de_contas_conectagran_${new Date().toISOString().slice(0, 10)}.txt`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Arquivo TXT exportado com sucesso!')
  }

  return (
    <div className="space-y-4 p-4">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Banco de Contas Sincronizadas</h2>
            <p className="text-xs text-muted-foreground">
              Acesse e copie e-mails e senhas de todas as contas criadas pelo aplicativo em qualquer dispositivo.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={exportTxt}
            variant="outline"
            className="flex items-center gap-2 text-xs border-primary/30 text-primary hover:bg-primary/10"
          >
            <Download className="h-4 w-4" />
            Exportar TXT ({profiles.length})
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por Nome, E-mail, UID ou Proxy..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 text-xs"
          />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Exibindo {filteredProfiles.length} de {profiles.length} conta(s)
        </span>
      </div>

      {/* Accounts Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-muted/50 text-muted-foreground font-semibold border-b border-border">
              <tr>
                <th className="p-3">Perfil / Identificador</th>
                <th className="p-3">E-mail de Login</th>
                <th className="p-3">Senha</th>
                <th className="p-3">UID (c_user)</th>
                <th className="p-3">Proxy Utilizado</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredProfiles.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    Nenhuma conta encontrada.
                  </td>
                </tr>
              ) : (
                filteredProfiles.map(profile => {
                  const isPassVisible = showPasswords[profile.id] ?? false
                  const emailVal = profile.fb_email || profile.name || '-'
                  const passVal = profile.fb_password || '-'
                  const uidVal = profile.fb_user_id || '-'
                  const proxyVal = profile.proxy || '-'

                  return (
                    <tr key={profile.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
                          <span>{profile.name}</span>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{emailVal}</span>
                          {profile.fb_email && (
                            <button
                              onClick={() => copyToClipboard(profile.fb_email!, `email-${profile.id}`, 'E-mail')}
                              className="text-muted-foreground hover:text-primary transition-colors p-1"
                              title="Copiar E-mail"
                            >
                              {copiedField === `email-${profile.id}` ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3 font-mono text-muted-foreground">
                        {profile.fb_password ? (
                          <div className="flex items-center gap-1.5">
                            <span>{isPassVisible ? passVal : '••••••••••••'}</span>
                            <button
                              onClick={() => togglePasswordVisibility(profile.id)}
                              className="text-muted-foreground hover:text-primary transition-colors p-1"
                              title={isPassVisible ? 'Ocultar Senha' : 'Mostrar Senha'}
                            >
                              {isPassVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </button>
                            <button
                              onClick={() => copyToClipboard(profile.fb_password!, `pass-${profile.id}`, 'Senha')}
                              className="text-muted-foreground hover:text-primary transition-colors p-1"
                              title="Copiar Senha"
                            >
                              {copiedField === `pass-${profile.id}` ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground/60 italic">Não gravada</span>
                        )}
                      </td>
                      <td className="p-3 font-mono text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{uidVal}</span>
                          {profile.fb_user_id && (
                            <button
                              onClick={() => copyToClipboard(profile.fb_user_id!, `uid-${profile.id}`, 'UID')}
                              className="text-muted-foreground hover:text-primary transition-colors p-1"
                              title="Copiar UID"
                            >
                              {copiedField === `uid-${profile.id}` ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs text-muted-foreground max-w-[200px] truncate" title={proxyVal}>
                        {proxyVal}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          onClick={() => {
                            const fullData = `E-mail: ${emailVal}\nSenha: ${passVal}\nUID: ${uidVal}\nProxy: ${proxyVal}`
                            copyToClipboard(fullData, `full-${profile.id}`, 'Dados completos')
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] text-primary hover:bg-primary/10"
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copiar Tudo
                        </Button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
